import { useEffect, useState } from "react";

import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { CheckedListControl } from "@/Common/Components/Inputs/CheckedListControl";
import { MultiDateSelector } from "@/Common/Components/Inputs/MultiDateSelector";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { SearchSettingsFooter } from "@/Common/Components/Settings/SearchSettingsFooter";
import { useLazyRef } from "@/Common/CustomHooks/useLazyRef";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { PrescriptionRenewalStatus } from "../Common/PrescriptionRenewalStatus";
import { PrescriptionRenewalFilterSettingModel } from "../Models/PrescriptionRenewalFilterSettingModel";

import styles from "./PrescriptionRenewalsFilterSettings.module.scss";

interface PrescriptionRenewalsFilterSettingsProps {
    settings: PrescriptionRenewalFilterSettingModel;
    onSaveChanges: (settings: PrescriptionRenewalFilterSettingModel, isSaveSettingsChecked: boolean) => void;
    onClose: () => void;
    onColumnOrderingPopupOpenClick: () => void;
}

const overlayKey: string = "PrescriptionRenewalFilterSettingPopupOverlay";

function PrescriptionRenewalsFilterSettings(props: PrescriptionRenewalsFilterSettingsProps): JSX.Element {
    const { getText } = useTranslation();
    const [settings, setSettings] = useState<PrescriptionRenewalFilterSettingModel>(props.settings ?? new PrescriptionRenewalFilterSettingModel());
    const [isSaveSettingsChecked, setIsSaveSettingsChecked] = useState<boolean>(false);
    const [filteredClinicians, setFilteredClinicians] = useState<EmployeeUIModel[]>([]);
    const [selectedClinicians, setSelectedClinicians] = useState<Immutable.List<EmployeeUIModel>>(Immutable.List([]));
    const prescriptionStatuses: PrescriptionRenewalStatus[] = [PrescriptionRenewalStatus.Completed, PrescriptionRenewalStatus.Pending, PrescriptionRenewalStatus.New];
    const orderedSites = useLazyRef(() => cacheStore.SiteCache.sortByProperty(sites => sites.Model.ClinicalName.toLocaleLowerCase()));
    const allClinicians = useLazyRef(() => cacheStore.EmployeeCache.map(p => EmployeeUIModel.fromCompositeObject(p)));

    const toggleSelectAllSites = (isAllSelected: boolean): void => {
        setSettings({ ...settings, selectedSitesIds: isAllSelected ? orderedSites.current.map(site => site.Model.Id) : [] });
    };

    const toggleSelectAllClinicians = (isAllSelected: boolean): void => {
        setSettings({ ...settings, selectedPhysiciansIds: isAllSelected ? filteredClinicians.map(clinician => clinician.Id) : [] });
    };

    const toggleSelectAllStatus = (isAllSelected: boolean): void => {
        setSettings({ ...settings, selectedStatus: isAllSelected ? prescriptionStatuses : [] });
    };

    const toggleSelectSite = (isChecked: boolean, siteId: string): void => {
        setSettings({
            ...settings, selectedSitesIds: isChecked ? [...settings.selectedSitesIds, siteId] :
                settings.selectedSitesIds.filter(selectedSiteId => selectedSiteId != siteId)
        });
    };

    const toggleSelectClinician = (isChecked: boolean, clinicianId: string): void => {
        setSettings({
            ...settings, selectedPhysiciansIds: isChecked ? [...settings.selectedPhysiciansIds, clinicianId] :
                settings.selectedPhysiciansIds.filter(selectedClinicianId => selectedClinicianId !== clinicianId)
        });
    };

    const toggleSelectStatus = (isChecked: boolean, status: PrescriptionRenewalStatus): void => {
        setSettings({
            ...settings, selectedStatus: isChecked ? [...settings.selectedStatus, status] :
                settings.selectedStatus.filter(selectedStatus => selectedStatus !== status)
        });
    };

    const onClinicanSearchTextChange = (newText: string): void => {
        setSettings({ ...settings, clinicianSearchText: newText });
    };

    const getStatusLabel = (status: PrescriptionRenewalStatus): string => {
        switch (status) {
            case PrescriptionRenewalStatus.New:
                return getText("NewLabel");
            case PrescriptionRenewalStatus.Pending:
                return getText("PendingLabel");
            case PrescriptionRenewalStatus.Completed:
                return getText("Completed2Label");
            default:
                return "";
        }
    };

    useEffect(() => {
        const selectedSitesIdsDictionary = settings?.selectedSitesIds.distinct().toDictionary(p => p as string);
        let filteredClinicianBySiteAndName = allClinicians.current;

        if (!!selectedSitesIdsDictionary && Object.keys(selectedSitesIdsDictionary).length) {
            filteredClinicianBySiteAndName = filteredClinicianBySiteAndName.filter(p => p.Sites.any(site => !!selectedSitesIdsDictionary[site]));
        }

        if (!IsNullOrWhiteSpace(settings?.clinicianSearchText)) {
            filteredClinicianBySiteAndName = filteredClinicianBySiteAndName.filterSearch(settings?.clinicianSearchText, p => p.Name);
        }

        setFilteredClinicians(filteredClinicianBySiteAndName);
    }, [settings?.clinicianSearchText, settings?.selectedSitesIds]);

    useEffect(() => {
        if (props.settings == null) {
            setSettings({
                ...settings,
                selectedSitesIds: orderedSites.current.map(site => site.Model.Id),
                selectedStatus: prescriptionStatuses
            });
        }
    }, []);

    useEffect(() => {
        overlayStore.showOverlay(overlayKey, {
            transparent: true,
            onClicked: () => {
                props.onSaveChanges(settings, isSaveSettingsChecked);
                props.onClose();
            }

        });
    }, [settings, isSaveSettingsChecked]);

    // eslint-disable-next-line arrow-body-style
    useEffect(() => {
        return () => {
            overlayStore.hideOverlay(overlayKey);
        };
    }, []);

    useEffect(() => {
        setSelectedClinicians(filteredClinicians.map(p => new EmployeeUIModel({ ...p, Checked: settings.selectedPhysiciansIds.any(id => p.Id === id) })).toImmutable());
    }, [filteredClinicians, settings.selectedPhysiciansIds]);

    return (
        <div className={`${styles.filterSettingsContainer} ${styles.inbox}`}>
            <div className={styles.headers}>
                <div>
                    {getText("DateLabel").toUpperCase()}
                </div>
                <div className="checkBoxHeader">
                    <CheckboxControl
                        label={getText("SitesLabel").toUpperCase()}
                        checked={settings?.selectedSitesIds.length === orderedSites.current.length}
                        onChange={isChecked => toggleSelectAllSites(isChecked)}
                        dataCy="select-all-sites"
                    />
                </div>
                <div className="checkBoxHeader">
                    <CheckboxControl
                        label={getText("CliniciansLabel").toUpperCase()}
                        checked={settings?.selectedPhysiciansIds?.length === filteredClinicians.length && filteredClinicians.length > 0}
                        onChange={isChecked => toggleSelectAllClinicians(isChecked)}
                        dataCy="select-all-clinicians"
                    />
                </div>
                <div className="checkBoxHeader">
                    <CheckboxControl
                        label={getText("StatusLabel").toUpperCase()}
                        checked={settings?.selectedStatus?.length === prescriptionStatuses.length}
                        onChange={isChecked => toggleSelectAllStatus(isChecked)}
                        dataCy="select-all-prescription-renewal-status"
                    />
                </div>
            </div>
            <div className={styles.columns}>
                <div className="date">
                    <MultiDateSelector
                        id="PrescriptionRenewalDates"
                        from={settings?.dateFrom}
                        to={settings?.dateTo}
                        fromSetTodayLabel={getText("TodayLabel")}
                        toSetTodayLabel={getText("TodayLabel")}
                        fromChange={date => setSettings({ ...settings, dateFrom: date })}
                        toChange={date => setSettings({ ...settings, dateTo: date })}
                    />
                </div>
                <div className={styles.elements}>
                    {orderedSites.current.map(site => (
                        <CheckboxControl
                            id={site.Model.Id}
                            label={site.Model.ClinicalName}
                            checked={settings?.selectedSitesIds.contains(site.Model.Id)}
                            onChange={isChecked => toggleSelectSite(isChecked, site.Model.Id)}
                            key={`prescriptionRenewal_${site.Model.Id}`}
                        />
                    ))}
                </div>
                <div className={styles.elements}>
                    <TextboxControl
                        className="form-control"
                        value={settings?.clinicianSearchText ?? ""}
                        onChange={(newText: string) => onClinicanSearchTextChange(newText)}
                        placeholder={getText("SearchLabel").toUpperCase()}
                    />
                    <CheckedListControl
                        dataCy="prescription-renewal-settings-clinician"
                        DataSource={selectedClinicians}
                        onChange={(isChecked: boolean, checkboxId: string) => toggleSelectClinician(isChecked, checkboxId)}
                        sort
                        limitTextWidth
                        idModifier="clinicians"
                        lazyRenderingContainerHeight={343}
                        enableLazyRendering
                    />
                </div>
                <div className={styles.elements}>
                    {prescriptionStatuses.map(status => (
                        <CheckboxControl
                            id={`status_${status}`}
                            label={getStatusLabel(status)}
                            checked={settings?.selectedStatus.contains(status)}
                            onChange={isChecked => toggleSelectStatus(isChecked, status)}
                            key={`status_${status}`}
                        />
                    ))}
                </div>
            </div>
            <div className={styles.footer}>
                <SearchSettingsFooter
                    onClose={() => props.onClose()}
                    onOk={() => props.onSaveChanges(settings, isSaveSettingsChecked)}
                    onColumnOrderingButtonClick={() => props.onColumnOrderingPopupOpenClick()}
                    onResultsPerPageChange={pageSize => setSettings({ ...settings, queryPageSize: pageSize })}
                    selectedPageSizeOption={settings?.queryPageSize}
                    onSaveSettingsCheckboxChange={checked => setIsSaveSettingsChecked(checked)}
                    isSaveSettingsChecked={isSaveSettingsChecked}
                    pageSizeOptions={[30, 50, 100]}
                />
            </div>
        </div>
    );
}

export { PrescriptionRenewalsFilterSettings };
